import { useEffect, useMemo } from "react"
import type { ReactNode } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { Link, useOutletContext } from "react-router-dom"
import { useTheme } from "../../theme/ThemeProvider"

type OnboardOutletContext = {
  step: number
  total: number
  goNext: () => void
  goPrev: () => void
  reducedMotion: boolean
}

interface OnboardLayoutProps {
  title?: string
  phonetic?: string
  subtitle?: string
  children: ReactNode
  contentWidth?: string | number
  canAdvance?: boolean
  onAdvance?: () => void
  enterLabel?: string
  showEnterHint?: boolean
  spriteSrc?: string
  accentDivider?: boolean
}

let railsAnimated = false
const RAIL_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]

function useOnboardOutlet(): OnboardOutletContext {
  return useOutletContext<OnboardOutletContext>()
}

export default function OnboardLayout({
  title,
  phonetic,
  subtitle,
  children,
  contentWidth = "min(640px, 100%)",
  canAdvance = true,
  onAdvance,
  enterLabel = "Press Enter to advance",
  showEnterHint = true,
  spriteSrc,
  accentDivider = true,
}: OnboardLayoutProps) {
  const { step, total, goNext, reducedMotion: contextReduced } = useOnboardOutlet()
  const reducedMotion = useReducedMotion() || contextReduced
  const shouldAnimateRails = !reducedMotion && !railsAnimated
  const { theme } = useTheme()
  const accentColor = "var(--accent)"
  const advance = onAdvance ?? goNext
  const releaseTag =
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ||
    (import.meta.env.VITE_COMMIT_SHA as string | undefined)
  const versionLabel = releaseTag ? releaseTag.slice(0, 8) : "dev"

  useEffect(() => {
    if (!railsAnimated) {
      railsAnimated = true
    }
  }, [])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const key = typeof event.key === "string" ? event.key.toLowerCase() : ""
      if (key === "enter" && canAdvance) {
        event.preventDefault()
        advance()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [canAdvance, advance])

  const sprite = useMemo(
    () => spriteSrc ?? (theme === "fairy" ? "/assets/pixel/fairy_16.png" : "/assets/pixel/demon_16.png"),
    [spriteSrc, theme],
  )

  const railProps = shouldAnimateRails
    ? { initial: { scaleX: 0 }, animate: { scaleX: 1 }, transition: { duration: 0.8, ease: RAIL_EASE } }
    : { initial: {}, animate: {} }

  const enterText = `[ ${enterLabel.toUpperCase()} ]`
  const resolvedContentWidth = typeof contentWidth === "number" ? `${contentWidth}px` : contentWidth

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        background: "var(--bg)",
        color: "var(--text)",
      }}
    >
      <motion.div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: "var(--rail)",
          transformOrigin: "left",
        }}
        {...railProps}
      />
      <motion.div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: "var(--rail)",
          transformOrigin: "left",
        }}
        {...railProps}
      />

      <header
        className="mono small"
        style={{ position: "absolute", top: "clamp(12px, 4vw, 20px)", left: "clamp(12px, 4vw, 24px)", letterSpacing: "0.12em" }}
      >
        ONBOARDING · STEP {String(step).padStart(2, "0")}/{total} · {versionLabel}
      </header>

      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // Top padding clears the absolutely-positioned step header with
          // generous air so the meta line never crowds the persona block.
          padding: `clamp(120px, 30vw, 150px) clamp(16px, 6vw, 48px) clamp(32px, 8vw, 48px)`,
        }}
      >
        <div
          style={{
            width: "min(960px, 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "clamp(20px, 5vw, 32px)",
          }}
        >
          {(title || phonetic || sprite) && (
            <div style={{ width: "min(640px, 100%)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "clamp(16px, 4vw, 24px)" }}>
                <Link
                  to="/"
                  aria-label="Back to the Sales Demon home page"
                  className="sprite-sigil"
                  style={{ boxShadow: `inset 0 0 0 1px ${accentColor}` }}
                >
                  <img src={sprite} alt="" className="pixel" style={{ width: 24, height: 24 }} />
                </Link>
                <div>
                  {title ? (
                    <h1
                      className="mono"
                      style={{
                        margin: 0,
                        fontSize: "clamp(32px, 8vw, 52px)",
                        letterSpacing: "0.5px",
                        lineHeight: 0.95,
                        fontWeight: 700,
                      }}
                    >
                      {title}
                    </h1>
                  ) : null}
                  {phonetic ? (
                    <div
                      className="mono small"
                      style={{
                        marginTop: "10px",
                        color: accentColor,
                        letterSpacing: "0.22em",
                      }}
                    >
                      {phonetic}
                    </div>
                  ) : null}
                </div>
              </div>
              {accentDivider ? (
                <div
                  style={{
                    marginTop: "clamp(12px, 3vw, 18px)",
                    width: "100%",
                    height: "1px",
                    background: accentColor,
                  }}
                />
              ) : (
                <div style={{ height: "12px" }} />
              )}
              {subtitle ? (
                <p
                  className="mono"
                  style={{
                    marginTop: "clamp(12px, 3vw, 18px)",
                    marginBottom: 0,
                    lineHeight: 1.6,
                    color: "var(--muted)",
                    maxWidth: "56ch",
                  }}
                >
                  {subtitle}
                </p>
              ) : null}
            </div>
          )}

          <section style={{ width: resolvedContentWidth, maxWidth: "100%" }}>{children}</section>

          {showEnterHint ? (
            <div
              style={{
                width: resolvedContentWidth,
                maxWidth: "100%",
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "clamp(32px, 8vw, 56px)",
              }}
            >
              <button
                type="button"
                onClick={advance}
                disabled={!canAdvance}
                className="mono small onboard-enter-btn"
                style={{
                  color: canAdvance ? accentColor : "rgba(139,139,145,0.4)",
                  letterSpacing: "0.18em",
                  background: "transparent",
                  border: "none",
                  cursor: canAdvance ? "pointer" : "not-allowed",
                  minHeight: "44px",
                }}
              >
                {enterText}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
